import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { validatePassword, getPasswordPolicyDescription } from '../../utils/validationUtils';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import PasswordFieldWithSideToggle from '../../components/common/PasswordFieldWithSideToggle';
import { showNotification } from '../../store/slices/uiSlice';
import { authService } from '../../services/authService';

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const dispatch = useDispatch();

  // 共通のパスワードバリデーション関数を使用するため、ローカル関数は削除

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // 入力検証
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('すべての項目を入力してください');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('新しいパスワードと確認用パスワードが一致しません');
      return;
    }

    // パスワードポリシーの検証
    const validationResult = validatePassword(newPassword);
    if (!validationResult.isValid) {
      setError(validationResult.errorMessage || 'パスワードが要件を満たしていません');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // パスワード変更処理
      const result = await authService.changePassword(currentPassword, newPassword);
      
      if (result.success) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        dispatch(showNotification({
          message: 'パスワードが正常に変更されました',
          type: 'success'
        }));
      } else {
        setError(result.message || 'パスワード変更に失敗しました');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'パスワード変更中にエラーが発生しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          パスワード変更
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>パスワードが正常に変更されました</Alert>}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <PasswordFieldWithSideToggle
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label="現在のパスワード"
            id="currentPassword"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
          />
          <PasswordFieldWithSideToggle
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="新しいパスワード"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
          />
          <PasswordFieldWithSideToggle
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="新しいパスワード（確認）"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {getPasswordPolicyDescription()}
          </Typography>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'パスワードを変更'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangePasswordPage;
